import { useEffect, useRef, useState, useCallback } from 'react';
import type { WSMessage, AuctionState, Item, Bid, Viewer, BinSale } from '../types';
import { ensureFreshToken } from '../api/client';

import { getWsUrl } from '../config/env';

export interface ActiveGiveaway {
  id: string;
  itemId: string;
  status: string;
  entryCount: number;
  startedAt?: string;
  winnerId?: string;
  winnerUsername?: string;
  item?: Item;
}

interface UseWebSocketReturn {
  isConnected: boolean;
  /** True once a SHOW_STATE has arrived — broadcastDeviceId is meaningless before this */
  hasReceivedState: boolean;
  viewerCount: number;
  viewers: Viewer[];
  onDeckItem: Item | null;
  activeAuction: AuctionState | null;
  activeBinSale: BinSale | null;
  recentBids: Bid[];
  auctionEnded: { winnerId: string | null; winnerUsername: string | null; winningBid: number | null } | null;
  showEnded: { reason: string } | null;
  activeGiveaway: ActiveGiveaway | null;
  giveawayEnded: { giveawayId: string; winnerId?: string; winnerUsername?: string; entryCount: number; cancelled: boolean } | null;
  clearGiveawayEnded: () => void;
  connect: (showId: string) => void;
  disconnect: () => void;
  // Multi-device broadcast
  connectionId: string | null;
  broadcastDeviceId: string | null;
  broadcasterPaused: boolean;
  claimBroadcast: () => void;
}

export function useWebSocket(): UseWebSocketReturn {
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [hasReceivedState, setHasReceivedState] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [viewers, setViewers] = useState<Viewer[]>([]);
  const [onDeckItem, setOnDeckItem] = useState<Item | null>(null);
  const [activeAuction, setActiveAuction] = useState<AuctionState | null>(null);
  const [activeBinSale, setActiveBinSale] = useState<BinSale | null>(null);
  const [recentBids, setRecentBids] = useState<Bid[]>([]);
  const [auctionEnded, setAuctionEnded] = useState<{
    winnerId: string | null;
    winnerUsername: string | null;
    winningBid: number | null;
  } | null>(null);
  const [showEnded, setShowEnded] = useState<{ reason: string } | null>(null);
  const [activeGiveaway, setActiveGiveaway] = useState<ActiveGiveaway | null>(null);
  const [giveawayEnded, setGiveawayEnded] = useState<{
    giveawayId: string;
    winnerId?: string;
    winnerUsername?: string;
    entryCount: number;
    cancelled: boolean;
  } | null>(null);

  // Multi-device broadcast
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [broadcastDeviceId, setBroadcastDeviceId] = useState<string | null>(null);
  const [broadcasterPaused, setBroadcasterPaused] = useState(false);

  // Reconnection state (SR-5). The seller's PINGs over this socket keep the
  // show's heartbeat alive — if the socket stays dead for 30s the backend
  // force-ends the show — so reconnection retries indefinitely (capped
  // backoff) until disconnect() or SHOW_ENDED.
  const shouldReconnectRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);
  const connectArgsRef = useRef<{ showId: string; userId?: string; username?: string } | null>(null);
  const lastMessageAtRef = useRef(0);
  // Bumped by every connect() attempt and by disconnect(); an attempt that
  // resumes after its token await with a stale generation bails out.
  const connectGenRef = useRef(0);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const connect = useCallback(async (showId: string, userId?: string, username?: string) => {
    connectArgsRef.current = { showId, userId, username };
    shouldReconnectRef.current = true;
    clearReconnectTimer();

    const state = wsRef.current?.readyState;
    if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
      return;
    }
    // wsRef is only assigned after the token await, so concurrent callers
    // (backoff timer, online, visibilitychange) would each pass the check
    // above. Only the latest attempt proceeds; disconnect() cancels all.
    const gen = ++connectGenRef.current;

    const params = new URLSearchParams();

    // The realtime engine identifies the connection from the access token only;
    // it no longer accepts a caller-supplied user_id.
    const token = await ensureFreshToken();
    if (gen !== connectGenRef.current || !shouldReconnectRef.current) return;
    if (!token) {
      console.warn('WebSocket: no access token available, not connecting');
      return;
    }
    params.set('token', token);

    lastMessageAtRef.current = 0;
    // broadcastDeviceId is stale until this socket's SHOW_STATE arrives —
    // AutoClaimBroadcast must not act on it in the meantime.
    setHasReceivedState(false);
    const ws = new WebSocket(`${getWsUrl()}/ws/show/${showId}?${params.toString()}`);

    ws.onopen = () => {
      console.log('WebSocket connected');
      reconnectAttemptsRef.current = 0;
      lastMessageAtRef.current = Date.now();
      setIsConnected(true);
    };

    ws.onclose = () => {
      if (wsRef.current !== ws) return; // superseded by a newer socket
      console.log('WebSocket disconnected');
      setIsConnected(false);
      if (!shouldReconnectRef.current || reconnectTimerRef.current !== null) return;
      const delay = Math.min(1000 * 2 ** reconnectAttemptsRef.current, 15000);
      reconnectAttemptsRef.current += 1;
      console.log(`WebSocket reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        const args = connectArgsRef.current;
        if (shouldReconnectRef.current && args) {
          connect(args.showId, args.userId, args.username);
        }
      }, delay);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onmessage = (event) => {
      lastMessageAtRef.current = Date.now();
      // Server may batch multiple messages separated by newlines
      const messages = (event.data as string).split('\n').filter(Boolean);
      for (const msgStr of messages) {
        try {
          const message: WSMessage = JSON.parse(msgStr);
          handleMessage(message);
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e, msgStr);
        }
      }
    };

    wsRef.current = ws;
  }, []);

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false;
    connectGenRef.current += 1;
    clearReconnectTimer();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    // onclose ignores the socket once wsRef no longer points at it, so clear
    // connected state here or the ping/watchdog interval keeps running.
    setIsConnected(false);
    setHasReceivedState(false);
  }, [clearReconnectTimer]);

  // Reconnect immediately when the network returns or the tab becomes
  // visible again — don't wait out the backoff timer.
  useEffect(() => {
    const tryReconnectNow = () => {
      if (!shouldReconnectRef.current || !connectArgsRef.current) return;
      const state = wsRef.current?.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return;
      clearReconnectTimer();
      reconnectAttemptsRef.current = 0;
      const args = connectArgsRef.current;
      connect(args.showId, args.userId, args.username);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') tryReconnectNow();
    };
    window.addEventListener('online', tryReconnectNow);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('online', tryReconnectNow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [connect, clearReconnectTimer]);

  const handleMessage = (message: WSMessage) => {
    console.log('WS Message:', message.type, message.payload);

    switch (message.type) {
      case 'CONNECTION_ACK': {
        const payload = message.payload as { viewer_count: number; connection_id: string };
        setViewerCount(payload.viewer_count);
        setConnectionId(payload.connection_id);
        break;
      }
      case 'SHOW_STATE': {
        const payload = message.payload as {
          viewer_count: number;
          viewers: Array<{ user_id: string; username: string }> | null;
          current_item: {
            id: string;
            name: string;
            description: string;
            image_url: string;
            starting_price?: number;
          } | null;
          auction: {
            id: string;
            item_id: string;
            current_price: number;
            highest_bidder_id: string | null;
            highest_bidder_username: string | null;
            bid_count: number;
            ends_at: string;
            milliseconds_remaining: number;
            auction_time_seconds: number;
            counterbid_time_seconds: number;
            sudden_death_enabled: boolean;
          } | null;
          active_giveaway?: {
            id: string;
            item_id: string;
            status: string;
            entry_count: number;
            started_at?: string;
            item?: { id: string; name: string; description?: string; image_url?: string };
          } | null;
          broadcast_device_id?: string;
        };
        setViewerCount(payload.viewer_count);
        // '' means "no one is broadcasting"; null means "unknown / not yet told"
        setBroadcastDeviceId(payload.broadcast_device_id || '');
        setHasReceivedState(true);

        // Map viewers from snake_case
        if (payload.viewers) {
          setViewers(payload.viewers.map(v => ({
            userId: v.user_id,
            username: v.username || null,
          })));
        }

        // Map current_item from snake_case
        if (payload.current_item) {
          setOnDeckItem({
            id: payload.current_item.id,
            name: payload.current_item.name,
            description: payload.current_item.description,
            imageUrl: payload.current_item.image_url,
            startingPrice: payload.current_item.starting_price || 0,
          });
        } else {
          setOnDeckItem(null);
        }

        // Map auction from snake_case
        if (payload.auction) {
          setActiveAuction({
            id: payload.auction.id,
            itemId: payload.auction.item_id,
            currentPrice: payload.auction.current_price,
            highestBidderId: payload.auction.highest_bidder_id,
            highestBidderUsername: payload.auction.highest_bidder_username,
            bidCount: payload.auction.bid_count,
            endsAt: payload.auction.ends_at,
            millisecondsRemaining: payload.auction.milliseconds_remaining,
            auctionTimeSeconds: payload.auction.auction_time_seconds,
            counterbidTimeSeconds: payload.auction.counterbid_time_seconds,
            suddenDeathEnabled: payload.auction.sudden_death_enabled,
          });
        } else {
          setActiveAuction(null);
        }

        // Map active_giveaway from snake_case
        if (payload.active_giveaway) {
          setActiveGiveaway({
            id: payload.active_giveaway.id,
            itemId: payload.active_giveaway.item_id,
            status: payload.active_giveaway.status,
            entryCount: payload.active_giveaway.entry_count,
            startedAt: payload.active_giveaway.started_at,
            item: payload.active_giveaway.item ? {
              id: payload.active_giveaway.item.id,
              name: payload.active_giveaway.item.name,
              description: payload.active_giveaway.item.description || '',
              imageUrl: payload.active_giveaway.item.image_url || '',
              startingPrice: 0,
            } : undefined,
          });
          setGiveawayEnded(null);
        } else {
          setActiveGiveaway(null);
        }
        break;
      }
      case 'ITEM_ON_DECK': {
        // Payload is the item directly, with snake_case fields
        const payload = message.payload as {
          id: string;
          name: string;
          description: string;
          image_url: string;
          starting_price?: number;
        };
        setOnDeckItem({
          id: payload.id,
          name: payload.name,
          description: payload.description,
          imageUrl: payload.image_url,
          startingPrice: payload.starting_price || 0,
        });
        setActiveAuction(null);
        setAuctionEnded(null);
        setActiveBinSale(null);
        break;
      }
      case 'AUCTION_STARTED': {
        // Payload is the auction state directly, with snake_case fields
        const payload = message.payload as {
          id: string;
          item_id: string;
          current_price: number;
          highest_bidder_id: string | null;
          highest_bidder_username: string | null;
          bid_count: number;
          ends_at: string;
          milliseconds_remaining: number;
          auction_time_seconds: number;
          counterbid_time_seconds: number;
          sudden_death_enabled: boolean;
        };
        setActiveAuction({
          id: payload.id,
          itemId: payload.item_id,
          currentPrice: payload.current_price,
          highestBidderUsername: payload.highest_bidder_username,
          highestBidderId: payload.highest_bidder_id,
          bidCount: payload.bid_count,
          endsAt: payload.ends_at,
          millisecondsRemaining: payload.milliseconds_remaining,
          auctionTimeSeconds: payload.auction_time_seconds,
          counterbidTimeSeconds: payload.counterbid_time_seconds,
          suddenDeathEnabled: payload.sudden_death_enabled,
        });
        setRecentBids([]);
        setAuctionEnded(null);
        break;
      }
      case 'NEW_BID': {
        // Payload is NewBidPayload with snake_case fields
        const payload = message.payload as {
          auction_id: string;
          bidder_id: string;
          bidder_username: string;
          amount: number;
          bid_count: number;
          milliseconds_remaining: number;
        };
        // Update auction state with new bid info
        setActiveAuction((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            currentPrice: payload.amount,
            highestBidderId: payload.bidder_id,
            highestBidderUsername: payload.bidder_username,
            bidCount: payload.bid_count,
            millisecondsRemaining: payload.milliseconds_remaining,
          };
        });
        // Add to recent bids (with deduplication)
        const newBid: Bid = {
          auctionId: payload.auction_id,
          bidderId: payload.bidder_id,
          bidderName: payload.bidder_username || '',
          amount: payload.amount,
          timestamp: new Date().toISOString(),
        };
        setRecentBids((prev) => {
          // Check if this exact bid already exists (same auction, bidder, amount)
          const isDuplicate = prev.some(
            (b) => b.auctionId === newBid.auctionId &&
                   b.bidderId === newBid.bidderId &&
                   b.amount === newBid.amount
          );
          if (isDuplicate) return prev;
          return [newBid, ...prev].slice(0, 10);
        });
        break;
      }
      case 'AUCTION_ENDING_SOON': {
        // Server sends seconds_remaining, convert to milliseconds and sync timer
        const payload = message.payload as { auction_id: string; seconds_remaining: number };
        const msRemaining = (payload.seconds_remaining || 0) * 1000;
        if (msRemaining > 0) {
          setActiveAuction((prev) => {
            if (!prev) return null;
            return { ...prev, millisecondsRemaining: msRemaining };
          });
        }
        break;
      }
      case 'AUCTION_ENDED': {
        const payload = message.payload as {
          auction_id: string;
          winner_id: string;
          winner_username: string;
          winning_bid: number;
          has_winner: boolean;
        };
        setAuctionEnded({
          winnerId: payload.has_winner ? payload.winner_id : null,
          winnerUsername: payload.has_winner ? payload.winner_username : null,
          winningBid: payload.has_winner ? payload.winning_bid : null,
        });
        setActiveAuction(null);
        break;
      }
      case 'SHOW_ENDED': {
        const payload = message.payload as {
          show_id: string;
          reason: string;
        };
        shouldReconnectRef.current = false; // don't reconnect to an ended show
        setShowEnded({ reason: payload.reason });
        setActiveAuction(null);
        setOnDeckItem(null);
        break;
      }
      case 'USER_JOINED': {
        const payload = message.payload as {
          user_id: string;
          username: string;
          viewer_count: number;
        };
        setViewerCount(payload.viewer_count);
        setViewers((prev) => {
          // Check if user already exists (might be reconnecting)
          if (prev.some(v => v.userId === payload.user_id)) {
            return prev;
          }
          return [...prev, { userId: payload.user_id, username: payload.username || null }];
        });
        break;
      }
      case 'USER_LEFT': {
        const payload = message.payload as {
          user_id: string;
          viewer_count: number;
        };
        setViewerCount(payload.viewer_count);
        setViewers((prev) => prev.filter(v => v.userId !== payload.user_id));
        break;
      }
      case 'VIEWER_COUNT': {
        const payload = message.payload as { viewer_count: number };
        setViewerCount(payload.viewer_count);
        break;
      }
      case 'BIN_SALE_STARTED':
      case 'BIN_SALE_STATE': {
        const payload = message.payload as {
          id: string;
          item_id: string;
          base_price: number;
          final_price: number;
          sale_enabled: boolean;
          sale_type?: string;
          sale_amount?: number;
          initial_quantity: number;
          remaining_quantity: number;
          status: string;
          item?: {
            id: string;
            name: string;
            description: string;
            image_url: string;
            starting_price?: number;
          };
        };
        // Use functional update to preserve existing item if payload doesn't include it
        setActiveBinSale((prev) => {
          const newItem = payload.item ? {
            id: payload.item.id,
            name: payload.item.name,
            description: payload.item.description || '',
            imageUrl: payload.item.image_url || '',
            startingPrice: payload.item.starting_price || 0,
          } : prev?.item; // Preserve existing item if not in payload

          return {
            id: payload.id,
            itemId: payload.item_id,
            basePrice: payload.base_price,
            finalPrice: payload.final_price,
            saleEnabled: payload.sale_enabled,
            saleType: payload.sale_type as 'PERCENT' | 'AMOUNT' | undefined,
            saleAmount: payload.sale_amount,
            initialQuantity: payload.initial_quantity,
            remainingQuantity: payload.remaining_quantity,
            status: payload.status as 'PENDING' | 'LIVE' | 'ENDED' | 'CANCELLED',
            item: newItem,
          };
        });
        setActiveAuction(null);
        setAuctionEnded(null);
        break;
      }
      case 'BIN_PURCHASE_SUCCESS': {
        const payload = message.payload as {
          sale_id: string;
          buyer_id: string;
          buyer_username?: string;
          order_id: string;
          price: number;
          remaining_quantity: number;
        };
        // Update remaining quantity
        setActiveBinSale((prev) => {
          if (!prev || prev.id !== payload.sale_id) return prev;
          return {
            ...prev,
            remainingQuantity: payload.remaining_quantity,
          };
        });
        break;
      }
      case 'BIN_PURCHASE_FAILED': {
        // Handle purchase failure - could show toast notification
        const payload = message.payload as {
          sale_id: string;
          buyer_id: string;
          reason: string;
        };
        console.log('BIN purchase failed:', payload.reason);
        break;
      }
      case 'BIN_SALE_ENDED': {
        const payload = message.payload as {
          sale_id: string;
          reason: string;
          total_sold: number;
        };
        console.log('BIN sale ended:', payload.reason, 'Total sold:', payload.total_sold);
        setActiveBinSale(null);
        break;
      }
      case 'GIVEAWAY_STARTED': {
        const payload = message.payload as {
          id: string;
          item_id: string;
          status: string;
          entry_count: number;
          started_at?: string;
          item?: { id: string; name: string; description?: string; image_url?: string };
        };
        setActiveGiveaway({
          id: payload.id,
          itemId: payload.item_id,
          status: payload.status,
          entryCount: payload.entry_count,
          startedAt: payload.started_at,
          item: payload.item ? {
            id: payload.item.id,
            name: payload.item.name,
            description: payload.item.description || '',
            imageUrl: payload.item.image_url || '',
            startingPrice: 0,
          } : undefined,
        });
        setGiveawayEnded(null);
        break;
      }
      case 'GIVEAWAY_ENTRY': {
        const payload = message.payload as {
          giveaway_id: string;
          entry_count: number;
          user_id: string;
          username: string;
        };
        setActiveGiveaway((prev) => {
          if (!prev || prev.id !== payload.giveaway_id) return prev;
          return { ...prev, entryCount: payload.entry_count };
        });
        break;
      }
      case 'GIVEAWAY_ENDED': {
        const payload = message.payload as {
          giveaway_id: string;
          winner_id?: string;
          winner_username?: string;
          entry_count: number;
          cancelled: boolean;
        };
        setGiveawayEnded({
          giveawayId: payload.giveaway_id,
          winnerId: payload.winner_id,
          winnerUsername: payload.winner_username,
          entryCount: payload.entry_count,
          cancelled: payload.cancelled,
        });
        setActiveGiveaway(null);
        break;
      }
      case 'BROADCAST_SOURCE_CHANGED': {
        const payload = message.payload as { device_id: string };
        setBroadcastDeviceId(payload.device_id || '');
        break;
      }
      case 'BROADCAST_REVOKED': {
        // Handled by the component via broadcastDeviceId change
        break;
      }
      case 'BROADCAST_PAUSED': {
        setBroadcasterPaused(true);
        break;
      }
      case 'BROADCAST_RESUMED': {
        setBroadcasterPaused(false);
        break;
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  // Ping to keep connection alive and maintain seller heartbeat
  // Must be more frequent than server's 30-second timeout
  useEffect(() => {
    if (!isConnected) return;

    // Send initial ping immediately to establish heartbeat
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'PING', payload: {} }));
    }

    const interval = setInterval(() => {
      // Dead-socket watchdog (SR-5): every PING gets a PONG, so with 10s pings
      // silence for 25s+ means the TCP connection silently died (e.g. network
      // switch) even though readyState still claims OPEN. Force-close to
      // trigger the reconnect path before the 30s heartbeat timeout ends the
      // show server-side.
      if (lastMessageAtRef.current && Date.now() - lastMessageAtRef.current > 25000) {
        console.warn('WebSocket silent for 25s — forcing reconnect');
        // Don't wait for onclose: on a dead TCP connection the browser only
        // fires it after its closing-handshake timeout (~60s in Chromium).
        // Detach the dead socket and reconnect now.
        const dead = wsRef.current;
        wsRef.current = null;
        if (dead) {
          dead.onclose = null;
          dead.onmessage = null;
          dead.onerror = null;
          dead.close();
        }
        setIsConnected(false);
        const args = connectArgsRef.current;
        if (shouldReconnectRef.current && args) {
          connect(args.showId, args.userId, args.username);
        }
        return;
      }
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'PING', payload: {} }));
      }
    }, 10000); // Ping every 10 seconds (well under 30-second timeout)

    return () => clearInterval(interval);
  }, [isConnected]);

  // Timer countdown (updates every 100ms for smooth display)
  useEffect(() => {
    if (!activeAuction || activeAuction.millisecondsRemaining <= 0) return;

    const interval = setInterval(() => {
      setActiveAuction((prev) => {
        if (!prev || prev.millisecondsRemaining <= 0) return prev;
        return {
          ...prev,
          millisecondsRemaining: Math.max(0, prev.millisecondsRemaining - 100),
        };
      });
    }, 100);

    return () => clearInterval(interval);
  }, [activeAuction?.id]);

  const claimBroadcast = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'CLAIM_BROADCAST', payload: {} }));
    }
  }, []);

  return {
    isConnected,
    hasReceivedState,
    viewerCount,
    viewers,
    onDeckItem,
    activeAuction,
    activeBinSale,
    recentBids,
    auctionEnded,
    showEnded,
    activeGiveaway,
    giveawayEnded,
    clearGiveawayEnded: useCallback(() => setGiveawayEnded(null), []),
    connect,
    disconnect,
    connectionId,
    broadcastDeviceId,
    broadcasterPaused,
    claimBroadcast,
  };
}
